import { useReducer, createContext, useState, useEffect } from "react";
import produce from "immer";
import {
  isCheckboxSelected,
  isFlueDrainDisabled,
  isHeaterDisabled,
  isLidCoverDisabled,
  isUpgradeDisabled,
  isWaterCareDisabled,
} from "./utils";
import {
  getLocalStorage,
  setLocalStorage,
} from "../../../helpers/local-storage";
import {withCorrectPrice} from "../../../helpers/utils";
import sections from './sections.json'

const initialState = {
  size: undefined,
  heaters: {
    heater: undefined,
    upgrades: [],
  },
  interiorexterior: {
    wood: undefined,
    woodTreatment: [],
    linerColor: undefined,
    linerType: undefined,
  },
  accessories: {
    lidCover: undefined,
    flueDrain: [],
    electrical: [],
    wooden: [],
    waterCare: [],
  },
};

const toggleCheckbox = (options, option, checked) => {
  const optionIndex = options.findIndex((upgrade) => upgrade.id === option.id);

  if (checked && optionIndex < 0) {
    options.push(option);
  }

  if (checked && optionIndex >= 0) {
    options.splice(optionIndex, 1, option);
  }

  if (!checked && optionIndex >= 0) {
    options.splice(optionIndex, 1);
  }
};

export const reducer = produce((draft, action) => {
  switch (action.type) {
    case "SELECT_SIZE": {
      draft.size = action.payload;

      if (isHeaterDisabled(draft.heaters.heater, action.payload)) {
        draft.heaters.heater = undefined;
        draft.heaters.upgrades = [];
      }

      break;
    }
    case "SELECT_HEATER": {
      const heater = action.payload.option
      draft.heaters.heater = heater
      const upgrades = draft.heaters.upgrades;

      draft.heaters.upgrades = upgrades.filter(
        (upgrade) =>
          isCheckboxSelected(upgrade, upgrades) &&
          !isUpgradeDisabled(upgrade, action.payload.option)
      );

      if (isLidCoverDisabled(draft.accessories.lidCover, action.payload.option, action.payload.upgrades)) {
        draft.accessories.lidCover = undefined;
      }

      const flueDrain = draft.accessories.flueDrain;
      draft.accessories.flueDrain = flueDrain.filter(
        (item) =>
          isCheckboxSelected(item, flueDrain) &&
          !isFlueDrainDisabled(item, action.payload.option)
      );

      const selectedUpgrades = [...action.payload.upgrades].filter((upr) => {
        if (heater?.id === 2 || heater?.id === 3) {
          return upr.id === 1 || upr.id === 2
        }
        if (!isUpgradeDisabled(upr, heater)) {
          return upr.id === 2
        }
      })

      if (heater.id === 5 || selectedUpgrades.length) {
        draft.heaters.upgrades = []
      }

      if (selectedUpgrades.length) {
        selectedUpgrades.forEach(upg => {
          if (upg.variations) {
            const item = {
                ...upg.variations[0],
                id: upg.id,
                label: upg.label,
                variant: upg.variations[0].label,
            }
            draft.heaters.upgrades.push(item)
          } else {
            draft.heaters.upgrades.push(upg)
          }
        })
      }

      break;
    }
    case "SELECT_HEATER_UPGRADE": {
      const { checked, option } = action.payload;
      toggleCheckbox(draft.heaters.upgrades, option, checked);
      break;
    }
    case "SELECT_WOOD": {
      draft.interiorexterior.wood = action.payload;
      break;
    }
    case "SELECT_WOOD_TREATMENT": {
      const { checked, option } = action.payload;
      toggleCheckbox(draft.interiorexterior.woodTreatment, option, checked);
      break;
    }
    case "SELECT_LINER_COLOR": {
      draft.interiorexterior.linerColor = action.payload;
      break;
    }
    case "SELECT_LINER_TYPE": {
      const {option} = action.payload
      draft.interiorexterior.linerType = option;
      if (option.id === 2 && (draft.heaters.heater && draft.heaters.heater.id === 1)) {
        draft.heaters.heater = undefined
      }
      break;
    }
    case "SELECT_LID_COVER": {
      draft.accessories.lidCover = action.payload;
      break;
    }
    case "SELECT_FLUE_DRAIN": {
      const { checked, option } = action.payload;
      toggleCheckbox(draft.accessories.flueDrain, option, checked);
      break;
    }
    case "SELECT_ELECTRICAL": {
      const { checked, option } = action.payload;
      toggleCheckbox(draft.accessories.electrical, option, checked);

      if (checked && option.id === 2) {
        const filtered = draft.accessories.electrical.filter(
          (item) => item?.variant !== "2.2kW with 10 jets"
        );

        draft.accessories.electrical = filtered;
      }

      break;
    }
    case "SELECT_WOODEN": {
      const { checked, option } = action.payload;
      toggleCheckbox(draft.accessories.wooden, option, checked);
      break;
    }
    case "SELECT_WATER_CARE": {
      const { checked, option } = action.payload;
      toggleCheckbox(draft.accessories.waterCare, option, checked);

      const waterCare = draft.accessories.waterCare;
      draft.accessories.waterCare = waterCare.filter(
        (item) =>
          isCheckboxSelected(item, waterCare) &&
          !isWaterCareDisabled(item, draft.accessories.waterCare)
      );
      break;
    }
    default:
      break;
  }
});


export const ConfigurationContext = createContext(initialState);
export const DispatchContext = createContext(undefined);

export const ConfigurationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    reducer,
    getLocalStorage("deluxeConfiguration", initialState)
  );

  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    const total =
      (withCorrectPrice(state.size)) +
      (withCorrectPrice(state.heaters.heater)) +
      state.heaters.upgrades.reduce((acc, cur) => acc + (withCorrectPrice(cur)), 0) +
      (withCorrectPrice(state.interiorexterior.wood)) +
      state.interiorexterior.woodTreatment.reduce(
        (acc, cur) => acc + (withCorrectPrice(cur)),
        0
      ) +
        (withCorrectPrice(state.interiorexterior.linerType)) +
      (withCorrectPrice(state.interiorexterior.linerColor)) +
      (withCorrectPrice(state.accessories.lidCover)) +
      state.accessories.flueDrain.reduce(
        (acc, cur) => acc + (withCorrectPrice(cur)),
        0
      ) +
      state.accessories.electrical.reduce(
        (acc, cur) => acc + (withCorrectPrice(cur)),
        0
      ) +
      state.accessories.wooden.reduce(
        (acc, cur) => acc + (withCorrectPrice(cur)),
        0
      ) +
      state.accessories.waterCare.reduce(
        (acc, cur) => acc + (withCorrectPrice(cur)),
        0
      );

    setTotalPrice(total);
    setLocalStorage("deluxeConfiguration", state);
  }, [state]);

  return (
    <ConfigurationContext.Provider value={{ ...state, totalPrice }}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </ConfigurationContext.Provider>
  );
};
